import {Button} from '@components/ui/button/Button'
import styled, {css, DefaultTheme} from 'styled-components'
import {Access} from '@/features/accesses/types'
import {Flexbox} from '@/components/ui/flexbox/FlexBox'
import {Text} from '@/components/ui/text/Text'

export const StyledAccordionWrapper = styled.div(
    ({theme: {spacing, palette}}) => css`
        display: flex;
        flex-direction: column;
        background-color: ${palette.neutral['white']};
        border-radius: ${spacing * 2}px;
        border: 1px solid ${palette.neutral['200']};
        padding: 0 ${spacing * 2}px;
    `
)

const rowGridTemplate = css`
    grid-template-columns: 40px repeat(6, minmax(0, 1fr)) 130px 108px;
`

/** The expanded access row carries one flexible column less than the collapsed one: it has no SIAR access cell. */
const accessRowGridTemplate = css`
    grid-template-columns: 40px repeat(5, minmax(0, 1fr)) 130px 108px;
`

export const StyledAccessesValidatorListItemContainer = styled.div(
    ({theme: {spacing}}) => css`
        display: grid;
        padding: ${spacing * 4}px;
        ${rowGridTemplate}
        column-gap: ${spacing * 4}px;
        align-items: center;
    `
)

/**
 * Grid items default to `min-width: auto`, so this cell would size to the full patient name and
 * spill over the next column instead of shrinking to its track, defeating the ellipsis below.
 * The cell is also rendered with `align="stretch"`: Flexbox defaults to `flex-start`, which would
 * size the name to its own content rather than to the cell.
 */
export const StyledPatientCell = styled(Flexbox)`
    min-width: 0;
    overflow: hidden;
`

/**
 * Pinned to the neutral palette: wrapping Text in styled() would otherwise instantiate its palette
 * generic to the whole PaletteKey union, narrowing `shade` to the handful of shades every palette shares.
 */
export const StyledTruncatedText = styled(Text<'neutral'>)`
    overflow: hidden;
    text-overflow: ellipsis;
    white-space: nowrap;
`

export const StyledAccessesValidatorListItemCounterWrapper = styled.div(
    ({theme: {palette, spacing}}) => css`
        display: flex;
        align-items: center;
        padding: ${spacing * 2}px;
        flex-direction: column;
        justify-content: center;
        background-color: ${palette.neutral['50']};
        border: 1px solid ${palette.neutral['200']};
        border-radius: ${spacing * 2}px;
        height: 100%;
        width: 100%;
    `
)

export const makeStatusSquareVariantStyle = (theme: DefaultTheme) =>
    ({
        validated: css`
            background-color: ${theme.palette.success['500']};
        `,
        to_validate: css`
            background-color: ${theme.palette.red['500']};
        `,
        excluded: css`
            background-color: ${theme.palette.neutral['500']};
        `,
        accounted: css`
            background-color: ${theme.palette.success['500']};
        `,
        discarded: css`
            background-color: ${theme.palette.neutral['800']};
        `
    }) as const satisfies Record<Access['status'], ReturnType<typeof css>>

export const StyledAccessesValidatorListItemStatusSquare = styled.div<{$status: Access['status']}>(
    ({theme, $status}) => css`
        width: ${theme.spacing * 2}px;
        height: ${theme.spacing * 2}px;
        border-radius: 2px;
        ${makeStatusSquareVariantStyle(theme)[$status]}
    `
)

export const StyledAccordionContentInner = styled.div(
    ({theme: {spacing}}) => css`
        padding: 0 ${spacing * 4}px ${spacing * 4}px ${spacing * 4}px;
    `
)

export const StyledOpenAccordionButton = styled(Button)<{$isExpanded: boolean}>(
    ({$isExpanded}) => css`
        & svg {
            transition: transform 0.2s ease-in-out;
            transform: ${$isExpanded ? 'rotate(180deg)' : 'rotate(0deg)'};
        }
    `
)

export const StyledAccordionItemContainer = styled.div(
    ({theme: {spacing}}) => css`
        display: grid;
        ${accessRowGridTemplate}
        align-items: center;
        column-gap: ${spacing * 4}px;
        padding: ${spacing * 3}px 0;
    `
)
